/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { format } from 'date-fns'
import { rrulestr } from 'rrule'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Button } from '~/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/ui/card'
import { Calendar } from '~/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '~/ui/popover'
import { Form } from '~/ui/form'
import { Checkbox } from '~/ui/checkbox'
import { gql } from '~/__generated__'
import { GraphqlError } from '~/ui/errors'
import { SelectField } from '~/fields/select-field'
import { CalendarIcon } from 'lucide-react'
import { cn } from '~/lib/utils'
import { Label } from '~/ui/label'
import { useSafeMutation } from '~/common/use-safe-mutation'
import { useNavigate, useParams } from 'react-router-dom'
import { schedulesPath } from '~/common/paths'
import invariant from 'tiny-invariant'
import { AllPlaylistsQuery, locationsForSelectQuery } from './new-schedule-screen'

export const playlistScheduleQuery = gql(`
  query PlaylistSchedule($id: ID!) {
    playlistSchedule(id: $id) {
      startDate
      endDate
      startTime
      endTime
      rrule
      zone {
        id
        name
        location {
          id
          name
          address
          timeZone
        }
      }
      playlist {
        id
        name
      }
    }
  }
`)

export const createPlaylistScheduleMutation = gql(`
  mutation UpsertPlaylistSchedule($input: UpsertPlaylistScheduleInput!) {
    upsertPlaylistSchedule(input: $input) {
      schedule {
        id
        startDate
        endDate
        startTime
        endTime
        rrule
        zone {
          id
          name
          location {
            id
            name
            address
            timeZone
          }
        }
        playlist {
          id
          name
        }
      }
    }
  }
`)

const formSchema = z.object({
  locationId: z.string(),
  zoneId: z.string(),
  playlistId: z.string(),
  startDate: z.string(),
  endDate: z.string().optional().nullable(),
  startTime: z.string(),
  endTime: z.string(),
  frequency: z.string().default(''),
  interval: z.string().default(''),
  byWeekday: z.string().default(''),
})

type FormValues = z.infer<typeof formSchema>

export const EditScheduleScreen = () => {
  const navigate = useNavigate()
  const { scheduleId } = useParams()
  invariant(scheduleId)

  const [selectedLocation, setSelectedLocation] = useState<any | null>(null)
  const [selectedZone, setSelectedZone] = useState<any | null>(null)

  const [createSchedule] = useSafeMutation(createPlaylistScheduleMutation)
  const { data: scheduleData, loading: scheduleLoading } = useQuery(playlistScheduleQuery, {
    variables: {
      id: scheduleId,
    },
  })
  const { data, loading, error } = useQuery(locationsForSelectQuery)
  const { data: allPlaylistsData } = useQuery(AllPlaylistsQuery)
  const locations = data?.locationsForSelect ?? []
  const allPlaylists = allPlaylistsData?.allPlaylists ?? []

  useEffect(() => {
    if (scheduleData?.playlistSchedule?.rrule) {
      try {
        const rule = rrulestr(scheduleData?.playlistSchedule?.rrule)
        console.log('rule:', rule)
        const frequency = rule.options.freq
        console.log('frequency:', frequency)
        const interval = rule.options.interval
        const frequencyMap: { [key: string]: string } = {
          0: 'yearly',
          1: 'monthly',
          2: 'weekly',
          3: 'daily',
          4: 'hourly',
        }
        const frequencyString = frequencyMap[frequency] ?? 'NONE'
        console.log('frequencyString:', frequencyString)
        form.setValue('frequency', frequencyString.toLowerCase())
        form.setValue('interval', interval.toString())
        const weekdays = Array.from(rule.options.byweekday.values()).map((day: any) => {
          // RRule uses 0=Monday, 1=Tuesday, ..., 6=Sunday
          // Convert to our format: 0=Sunday, 1=Monday, ..., 6=Saturday
          const rruleToFormMap: { [key: string]: string } = {
            '0': '1', // RRule Monday -> Form Monday
            '1': '2', // RRule Tuesday -> Form Tuesday
            '2': '3', // RRule Wednesday -> Form Wednesday
            '3': '4', // RRule Thursday -> Form Thursday
            '4': '5', // RRule Friday -> Form Friday
            '5': '6', // RRule Saturday -> Form Saturday
            '6': '0', // RRule Sunday -> Form Sunday
          }
          return rruleToFormMap[day.toString()] || day.toString()
        })
        form.setValue('byWeekday', weekdays.join(','))
      } catch (error) {
        console.error('Error parsing RRule:', error)
      }
    }
  }, [scheduleData?.playlistSchedule?.rrule])

  useEffect(() => {
    if (scheduleData?.playlistSchedule?.zone) {
      setSelectedLocation(scheduleData?.playlistSchedule?.zone?.location)
      setSelectedZone(scheduleData?.playlistSchedule?.zone)
      form.setValue('locationId', scheduleData?.playlistSchedule?.zone?.location?.id ?? '')
      form.setValue('zoneId', scheduleData?.playlistSchedule?.zone?.id ?? '')
      form.setValue('playlistId', scheduleData?.playlistSchedule?.playlist?.id ?? '')
      form.setValue('startDate', scheduleData?.playlistSchedule?.startDate ?? '')
      form.setValue('endDate', scheduleData?.playlistSchedule?.endDate)
      form.setValue('startTime', scheduleData?.playlistSchedule?.startTime ?? '')
      form.setValue('endTime', scheduleData?.playlistSchedule?.endTime ?? '')
    }
  }, [scheduleData])

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      locationId: scheduleData?.playlistSchedule?.zone?.location?.id,
      zoneId: scheduleData?.playlistSchedule?.zone?.id,
      playlistId: scheduleData?.playlistSchedule?.playlist?.id,
      startDate: scheduleData?.playlistSchedule?.startDate ?? new Date().toISOString(),
      endDate: scheduleData?.playlistSchedule?.endDate,
      startTime: scheduleData?.playlistSchedule?.startTime,
      endTime: scheduleData?.playlistSchedule?.endTime,
      frequency: '',
      interval: '',
      byWeekday: '',
    },
  })

  const locationId = form.watch('locationId')
  const zoneId = form.watch('zoneId')
  const startDate = form.watch('startDate')
  const endDate = form.watch('endDate')
  const frequency = form.watch('frequency')
  const byWeekday = form.watch('byWeekday')

  const onSubmit = async (values: FormValues) => {
    // Handle form submission logic here
    console.log('Form submitted with values:', values)
    createSchedule({
      variables: {
        input: {
          id: scheduleId,
          attrs: {
            zoneId: values.zoneId,
            playlistId: values.playlistId,
            startDate: values.startDate,
            endDate: values.endDate,
            startTime: values.startTime,
            endTime: values.endTime,
            frequency:
              values.frequency === 'none' ? undefined : (values.frequency.toUpperCase() as any),
            interval: values.interval ? Number(values.interval) : 1,
            byWeekday: values.byWeekday
              ? values.byWeekday
                  .split(',')
                  .filter(Boolean)
                  .map((day) => {
                    // Convert from our form format to RRule format
                    // Form: 0=Sunday, 1=Monday, ..., 6=Saturday
                    // RRule: 0=Monday, 1=Tuesday, ..., 6=Sunday
                    const formToRRuleMap: { [key: string]: string } = {
                      '0': 'SUNDAY', // Form Sunday -> RRule Sunday
                      '1': 'MONDAY', // Form Monday -> RRule Monday
                      '2': 'TUESDAY', // Form Tuesday -> RRule Tuesday
                      '3': 'WEDNESDAY', // Form Wednesday -> RRule Wednesday
                      '4': 'THURSDAY', // Form Thursday -> RRule Thursday
                      '5': 'FRIDAY', // Form Friday -> RRule Friday
                      '6': 'SATURDAY', // Form Saturday -> RRule Saturday
                    }
                    return formToRRuleMap[day] as any
                  })
                  .filter(Boolean)
              : [],
          },
        },
      },
    })
      .then((response) => {
        if (response.errors) {
          console.error('Error creating schedule:', response.errors)
          // Optionally, you can show an error message to the user
          return
        }

        console.log('Schedule created successfully:', response)
        form.reset()
        navigate(schedulesPath({}))
      })
      .catch((error) => {
        console.error('Error creating schedule:', error)
        // Optionally, you can show an error message to the user
      })
  }

  const zonesForLocation = (locationId: string) => {
    const location = locations.find((location) => location.id === locationId)
    if (!location) {
      console.error(`Location with id ${locationId} not found`)
      return []
    }
    return location.zones ?? []
  }

  const generateTimeOptions = () => {
    return Array.from({ length: 96 }).map((_, i) => {
      const hour24 = Math.floor(i / 4)
      const minute = ((i % 4) * 15).toString().padStart(2, '0')

      // Convert to 12-hour format
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
      const period = hour24 < 12 ? 'AM' : 'PM'

      return {
        value: `${hour24.toString().padStart(2, '0')}:${minute}`, // 24-hour for form value
        label: `${hour12}:${minute} ${period}`, // 12-hour for display
      }
    })
  }

  const generateIntervalOptions = (frequency: string) => {
    let maxOptions: number

    switch (frequency) {
      case 'hourly':
        maxOptions = 24 // 1-24 hours
        break
      case 'daily':
        maxOptions = 31 // 1-31 days
        break
      case 'weekly':
        maxOptions = 52 // 1-52 weeks
        break
      case 'monthly':
        maxOptions = 12 // 1-12 months
        break
      case 'yearly':
        maxOptions = 10 // 1-10 years (reasonable limit)
        break
      default:
        return []
    }

    const baseOptions = Array.from({ length: maxOptions }, (_, i) => i + 1)

    switch (frequency) {
      case 'hourly':
        return baseOptions.map((num) => ({
          value: num.toString(),
          label: num === 1 ? 'Every hour' : `Every ${num} hours`,
        }))

      case 'daily':
        return baseOptions.map((num) => ({
          value: num.toString(),
          label: num === 1 ? 'Every day' : `Every ${num} days`,
        }))

      case 'weekly':
        return baseOptions.map((num) => ({
          value: num.toString(),
          label: num === 1 ? 'Every week' : `Every ${num} weeks`,
        }))

      case 'monthly':
        return baseOptions.map((num) => ({
          value: num.toString(),
          label: num === 1 ? 'Every month' : `Every ${num} months`,
        }))

      case 'yearly':
        return baseOptions.map((num) => ({
          value: num.toString(),
          label: num === 1 ? 'Every year' : `Every ${num} years`,
        }))

      default:
        return []
    }
  }

  const generateWeekdayOptions = () => {
    return [
      { value: '0', label: 'Sunday' },
      { value: '1', label: 'Monday' },
      { value: '2', label: 'Tuesday' },
      { value: '3', label: 'Wednesday' },
      { value: '4', label: 'Thursday' },
      { value: '5', label: 'Friday' },
      { value: '6', label: 'Saturday' },
    ]
  }

  if (error) {
    return <GraphqlError error={error} />
  }

  if (loading || scheduleLoading) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-6">
        <h1 className="text-2xl">Loading...</h1>
      </div>
    )
  }

  return (
    <div className="h-screen w-full overflow-y-auto">
      <div className="flex items-center justify-between border-b px-6 py-3">
        <h1 className="text-2xl">New Schedule</h1>
      </div>
      <div className="p-6 pb-24">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid w-full grid-cols-1 gap-6">
              <Card className="col-span-1 w-full gap-4">
                <CardHeader>
                  <CardTitle>Zone / Playlist</CardTitle>
                </CardHeader>
                <CardContent className="grid w-full grid-cols-3 gap-4">
                  <SelectField
                    control={form.control}
                    name="locationId"
                    label="Location"
                    labelClassName="mb-2"
                    containerClassName="col-span-1 w-full"
                    placeholder="Select Location"
                    selectEntries={
                      locations.map((location) => ({
                        value: location.id,
                        label: location.name,
                      })) ?? []
                    }
                    required
                  />
                  {locationId && (
                    <SelectField
                      control={form.control}
                      name="zoneId"
                      label="Zone"
                      labelClassName="mb-2"
                      placeholder="Select Zone"
                      selectEntries={
                        zonesForLocation(locationId)?.map((zone: any) => ({
                          value: zone.id,
                          label: zone.name,
                        })) ?? []
                      }
                      required
                    />
                  )}
                  {locationId && zoneId && (
                    <SelectField
                      control={form.control}
                      name="playlistId"
                      label="Playlist"
                      labelClassName="mb-2"
                      placeholder="Select Playlist"
                      selectEntries={
                        allPlaylists?.map((playlist: any) => ({
                          value: playlist.id,
                          label: `${playlist.name} (${playlist.tracksCount} tracks)`,
                        })) ?? []
                      }
                      required
                    />
                  )}
                </CardContent>
              </Card>
              <Card className="col-span-1 w-full gap-4">
                <CardHeader>
                  <CardTitle>Schedule</CardTitle>
                </CardHeader>
                <CardContent className="grid w-full grid-cols-2 gap-6">
                  <Popover>
                    <div className="flex flex-col gap-2">
                      <Label>
                        Start Date<span className="ml-1 text-red-500">*</span>
                      </Label>
                      <PopoverTrigger asChild>
                        <Button
                          variant={'outline'}
                          className={cn(
                            'justify-start text-left font-normal',
                            !startDate && 'text-muted-foreground'
                          )}
                        >
                          <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                          {startDate ? (
                            format(new Date(startDate), 'PPP')
                          ) : (
                            <span>Pick a date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                    </div>
                    <PopoverContent className="flex w-auto items-start p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate ? new Date(startDate) : undefined}
                        onSelect={(selectedDate: Date | undefined) => {
                          if (selectedDate) {
                            form.setValue('startDate', selectedDate.toISOString())
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <div className="flex flex-col gap-2">
                      <Label>End Date</Label>
                      <PopoverTrigger asChild>
                        <Button
                          variant={'outline'}
                          className={cn(
                            'justify-start text-left font-normal',
                            !endDate && 'text-muted-foreground'
                          )}
                        >
                          <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                          {endDate ? format(new Date(endDate), 'PPP') : <span>Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                    </div>
                    <PopoverContent className="flex w-auto items-start p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate ? new Date(endDate) : undefined}
                        onSelect={(selectedDate: Date | undefined) => {
                          if (selectedDate) {
                            form.setValue('endDate', selectedDate.toISOString())
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <SelectField
                    control={form.control}
                    name="startTime"
                    label="Start Time"
                    labelClassName="mb-2"
                    placeholder="Select Start Time"
                    selectEntries={generateTimeOptions()}
                    required
                  />
                  <SelectField
                    control={form.control}
                    name="endTime"
                    label="End Time"
                    labelClassName="mb-2"
                    placeholder="Select End Time"
                    selectEntries={generateTimeOptions()}
                    required
                  />
                  <hr className="mt-4" />
                  <Label className="col-span-2">Recurring Configuration</Label>
                  <SelectField
                    control={form.control}
                    name="frequency"
                    label="Frequency"
                    labelClassName="mb-2"
                    placeholder="Select Frequency"
                    selectEntries={[
                      { value: 'none', label: 'Non-Recurring' },
                      { value: 'hourly', label: 'Hourly' },
                      { value: 'daily', label: 'Daily' },
                      { value: 'weekly', label: 'Weekly' },
                      { value: 'monthly', label: 'Monthly' },
                      { value: 'yearly', label: 'Yearly' },
                    ]}
                  />
                  {frequency && frequency !== 'none' && (
                    <>
                      <SelectField
                        control={form.control}
                        name="interval"
                        label="Interval"
                        labelClassName="mb-2"
                        placeholder="Select Interval"
                        selectEntries={generateIntervalOptions(frequency)}
                      />
                      {frequency !== 'daily' && frequency !== 'hourly' && (
                        <div className="col-span-2">
                          <Label className="mb-2 block">Days of the Week</Label>
                          <div className="grid grid-cols-4 gap-3">
                            {generateWeekdayOptions().map((day) => {
                              const selectedDays = byWeekday ? byWeekday.split(',') : []
                              const isChecked = selectedDays.includes(day.value)

                              return (
                                <div key={day.value} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`weekday-${day.value}`}
                                    checked={isChecked}
                                    onCheckedChange={(checked) => {
                                      const currentDays = byWeekday
                                        ? byWeekday.split(',').filter(Boolean)
                                        : []
                                      let newDays

                                      if (checked) {
                                        newDays = [...currentDays, day.value]
                                      } else {
                                        newDays = currentDays.filter((d) => d !== day.value)
                                      }

                                      form.setValue('byWeekday', newDays.join(','))
                                    }}
                                  />
                                  <label
                                    htmlFor={`weekday-${day.value}`}
                                    className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                  >
                                    {day.label}
                                  </label>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
              <Button variant="default" type="submit" className="w-full cursor-pointer">
                <span className="text-sm">Update Schedule</span>
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}
